import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLFormElement} [form] - The form element (for inline search).
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  /**
   * Removes vendor filter from search query string
   * @param {string} query - The search query
   * @returns {string} The query without vendor filter
   */
  #removeVendorFilter(query) {
    if (!query) return '';
    // Remove vendor:TegelMegaStore from the query (case insensitive)
    return query
      .replace(/\s*vendor:TegelMegaStore\s*/gi, ' ')
      .replace(/\s*vendor:TegelMegaStore$/gi, '')
      .replace(/^vendor:TegelMegaStore\s*/gi, '')
      .replace(/^vendor:TegelMegaStore$/gi, '')
      .trim();
  }

  /**
   * Cleans the input value by removing vendor filter
   */
  #cleanInputValue = () => {
    if (!this.refs.searchInput) return;
    
    const currentValue = this.refs.searchInput.value;
    const cleanedValue = this.#removeVendorFilter(currentValue);
    
    if (cleanedValue !== currentValue) {
      this.refs.searchInput.value = cleanedValue;
      // Update reset button visibility
      if (cleanedValue.length > 0) {
        this.#showResetButton();
      } else {
        this.#hideResetButton();
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    // Remove vendor filter from input value if present
    if (this.refs.searchInput.value) {
      const cleanedValue = this.#removeVendorFilter(this.refs.searchInput.value);
      if (cleanedValue !== this.refs.searchInput.value) {
        this.refs.searchInput.value = cleanedValue;
      }
    }

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // For inline search: prevent dropdown from closing when clicking on links
    if (!dialog && this.refs.predictiveSearchResults) {
      this.refs.predictiveSearchResults.addEventListener('pointerdown', this.#handleResultsPointerDown, { signal });
      this.refs.predictiveSearchResults.addEventListener('click', this.#handleResultsClick, { signal, capture: true });
      this.refs.searchInput.addEventListener('blur', this.#handleInputBlur, { signal });
      // Handle clicks outside the search component
      document.addEventListener('click', this.#handleDocumentClick, { signal });
    }

    // Add vendor filter to form submission
    const form = this.#formElement;
    if (form) {
      form.addEventListener('submit', this.#handleFormSubmit, { signal });
    }

    // Clean input value when URL changes (e.g., browser back/forward)
    window.addEventListener('popstate', this.#cleanInputValue, { signal });
    
    // Also check on page load if value came from URL
    onDocumentLoaded(() => {
      this.#cleanInputValue();
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Flag to track if a link is being clicked in the results
   * @type {boolean}
   */
  #isClickingLink = false;

  /**
   * Timeout to delay blur handling
   * @type {number | null}
   */
  #blurTimeout = null;

  /**
   * Gets the form element for inline search
   * @returns {HTMLElement | null}
   */
  get #formElement() {
    return this.refs.form || this.querySelector('.predictive-search-form--inline');
  }

  /**
   * Handles pointerdown events on results to prevent dropdown from closing
   * @param {PointerEvent} event - The pointer event
   */
  #handleResultsPointerDown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const link = target.closest('a, .resource-card, .resource-card__link');
    
    if (link) {
      this.#isClickingLink = true;
      // Set data attribute on form element to keep dropdown open
      const form = this.#formElement;
      if (form) {
        form.setAttribute('data-keep-open', 'true');
      }
      // Clear any pending blur timeout
      if (this.#blurTimeout) {
        clearTimeout(this.#blurTimeout);
        this.#blurTimeout = null;
      }
    }
  };

  /**
   * Handles click events on results to ensure links are clickable
   * @param {MouseEvent} event - The mouse event
   */
  #handleResultsClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const link = target.closest('a, .resource-card');
    
    if (link) {
      // If it's a resource-card, find the actual link inside
      const actualLink = link instanceof HTMLAnchorElement 
        ? link 
        : link.querySelector('a.resource-card__link, a[href]');
      
      if (actualLink && actualLink.href) {
        // Allow the link to be clicked normally
        // The navigation will happen naturally
        // Remove the data attribute after a short delay to allow navigation
        const form = this.#formElement;
        setTimeout(() => {
          this.#isClickingLink = false;
          if (form) {
            form.removeAttribute('data-keep-open');
          }
        }, 100);
      }
    }
  };

  /**
   * Handles blur events on the input to prevent dropdown from closing when clicking links
   * @param {FocusEvent} event - The focus event
   */
  #handleInputBlur = (event) => {
    const form = this.#formElement;
    if (!form) return;
    
    // Clear any existing timeout
    if (this.#blurTimeout) {
      clearTimeout(this.#blurTimeout);
    }
    
    // If we're clicking on a link, keep the dropdown open using data attribute
    if (this.#isClickingLink) {
      // The data-keep-open attribute will keep the dropdown visible
      // Don't close the dropdown - let the click proceed
      // Reset the flag after navigation
      this.#blurTimeout = setTimeout(() => {
        this.#isClickingLink = false;
        form.removeAttribute('data-keep-open');
        this.#blurTimeout = null;
      }, 300);
      return;
    }
    
    // Check if the new focus target is within the search results
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && this.contains(relatedTarget)) {
      // Focus moved to something inside the search component, keep it open
      form.setAttribute('data-keep-open', 'true');
      return;
    }
    
    // If not clicking a link and focus moved outside, remove the data attribute to allow normal closing
    // But add a delay to allow click events to fire first
    this.#blurTimeout = setTimeout(() => {
      if (!this.#isClickingLink) {
        form.removeAttribute('data-keep-open');
      }
      this.#blurTimeout = null;
    }, 200);
  };

  /**
   * Handles document clicks to close dropdown when clicking outside
   * @param {MouseEvent} event - The mouse event
   */
  #handleDocumentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const form = this.#formElement;
    
    // If clicking outside the search component and not on a link within it
    if (!this.contains(target) && !this.#isClickingLink && form) {
      form.removeAttribute('data-keep-open');
    }
  };

  /**
   * Handles form submission to navigate to search results page
   * Note: Vendor filtering is done in Liquid rendering, not in the query
   * @param {SubmitEvent} event - The submit event
   */
  #handleFormSubmit = (event) => {
    event.preventDefault();
    
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const searchInput = form.querySelector('input[name="q"]');
    if (!(searchInput instanceof HTMLInputElement)) return;

    const currentQuery = searchInput.value.trim();
    
    // Build URL without vendor filter - filtering is done in Liquid rendering
    const searchUrl = new URL(form.action || Theme.routes.search_url, location.origin);
    if (currentQuery) {
      searchUrl.searchParams.set('q', currentQuery);
    }
    
    window.location.href = searchUrl.toString();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          const currentQuery = this.refs.searchInput.value.trim();
          // Don't add vendor filter - filtering is done in Liquid rendering
          if (currentQuery) {
            searchUrl.searchParams.set('q', currentQuery);
          }
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * Note: Vendor filtering is done in Liquid rendering, not in the query
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    // Don't add vendor filter - filtering is done in Liquid rendering for consistency
    if (searchTerm) {
      url.searchParams.set('q', searchTerm);
    }
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    // Recently viewed products functionality disabled - only show results based on input

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
